import { LegacyCardContent, LegacyCardHeader } from "../../card/LegacyCard";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useOrderInvoiceUrl } from "../../../hooks/api/v2/order/useOrderInvoiceUrl";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";

import { BuyerOnly } from "../../authFilter/BuyerOnly";
import { Anchor, Button, H5, Icon } from "@sellar-io/components";
import { OrderStage } from "../helpers";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../table/TableLegacy";
import { capitalize, formatCurrency } from "@sellar-io/common";
import { formatDate } from "../../../utils/formatDate";
import { OrderPaymentStatusLegacy } from "../OrderPaymentStatus/OrderPaymentStatusLegacy";
import { OrderPaymentMethod } from "../OrderPaymentMethod";
import { generateOrderPaymentMethodText } from "../OrderPaymentMethod.shared";
import { SupplierOnly } from "../../authFilter/SupplierOnly";
import { useBuildStripeDashboardUrl } from "../../../utils/stripeDashboardUrl";
import { ViewPaymentLinkModal } from "../../../pages/order/modals/ViewPaymentLinkModal";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";

export type OrderSummaryPaymentProps = {
  fulfillmentType: OrderGetResponseType["fulfillmentType"];
  stage: OrderStage;
  paidAt?: Date | null;
  paymentDue?: Date | null;
  paymentTerms?: "credit" | "proforma" | "paymentOnDelivery" | null;
  invoiceFinalizedAt: Date | null;
  cancelledAt?: Date;
  cancelledBy?: string;
  orderId: number;
  stripeInvoiceId?: string | null;
  paymentStatus: OrderGetResponseType["paymentStatus"];
  paymentType: OrderGetResponseType["paymentType"];
  invoiceCreationStage: OrderGetResponseType["Supplier"]["invoiceCreationStage"];
  confirmedAt: Date | null;
  deliveredAt: Date | null;
  dispatchedAt: Date | null;
  orderPrice: number;
  paymentList: OrderGetResponseType["PaymentList"];
  supplierStripeAccountId?: string;
  hasPaymentLink: boolean;
  paymentLinkCode?: string | null;
  retailerInvoiceEmails?: string[];
  supplierName?: string;
};

export const OrderPaymentSummary = (
  props: OrderSummaryPaymentProps,
): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const buildStripeDashboardUrl = useBuildStripeDashboardUrl();
  const [viewPaymentLinkActive, setViewPaymentLinkActive] = useState(false);
  const invoiceUrlQuery = useOrderInvoiceUrl(props.orderId, {
    enabled:
      !!(
        props.invoiceFinalizedAt ||
        props.paymentStatus === "open" ||
        props.paymentStatus === "paid"
      ) &&
      !!business?.Retailer &&
      !!props.stripeInvoiceId,
  });

  const payStripeInvoice = () => {
    if (invoiceUrlQuery.data?.invoiceUrl) {
      window.open(invoiceUrlQuery.data.invoiceUrl, "_blank");
    }
  };

  const totalPaid = props.paymentList.reduce(
    (total, payment) =>
      payment.status === "paid" ? total + payment.amount : total,
    0,
  );

  // A cardPaymentWithDeposit remaining balance paid manually (outside of
  // Stripe) never gets its own "paid" Payment row - the real card Payment
  // attempt stays failed, which is correct - so totalPaid alone
  // under-reports what's been settled. paymentStatus is the authoritative
  // "has this order been paid" signal for both the automated card-charge
  // path and the manual-payment path.
  const isCardPaymentWithDepositPaid =
    props.paymentType === "cardPaymentWithDeposit" &&
    props.paymentStatus === "paid";
  const remainingBalancePaidManually =
    isCardPaymentWithDepositPaid &&
    !props.paymentList.some(
      (payment) => payment.status === "paid" && payment.type === null,
    );

  const remainingBalance = isCardPaymentWithDepositPaid
    ? 0
    : props.orderPrice - totalPaid;

  const hasPayment = props.paymentList.length > 0;
  const showPaymentLinkPaymentList = Boolean(props.hasPaymentLink);
  const showPaymentList =
    hasPayment &&
    (props.paymentType === "cardPaymentWithDeposit" ||
      props.paymentType === "checkout" ||
      showPaymentLinkPaymentList);

  const isImplicitProformaEnabled =
    useRemoteFeatureFlags().isActive("implicitProforma");
  const isImplicitProforma =
    isImplicitProformaEnabled &&
    !!business?.Retailer &&
    props.paymentType === "manual" &&
    !props.hasPaymentLink;

  return (
    <>
      <div>
        <LegacyCardHeader>
          <div className={"flex items-center justify-between"}>
            <H5>Payment</H5>
            <BuyerOnly>
              {props.paymentType === "stripe" &&
                props.paymentStatus !== "paid" &&
                !props.cancelledAt &&
                !props.cancelledBy && (
                  <Button
                    disabled={
                      !props.stripeInvoiceId ||
                      (!props.invoiceFinalizedAt &&
                        props.paymentStatus !== "open")
                    }
                    onClick={() => payStripeInvoice()}
                  >
                    <Icon size={0.7} icon={"CREDIT_CARD"} />
                    Pay by card
                  </Button>
                )}
            </BuyerOnly>
            <SupplierOnly>
              {props.hasPaymentLink && props.paymentLinkCode && (
                <Button
                  color={"secondary"}
                  onClick={() => setViewPaymentLinkActive(true)}
                >
                  <Icon size={0.7} icon={"CREDIT_CARD"} />
                  View payment link
                </Button>
              )}
            </SupplierOnly>
          </div>
        </LegacyCardHeader>
        {showPaymentList ? (
          <LegacyCardContent className="flex flex-col gap-4">
            <TableLegacyContainer className={"max-h-[300px]"}>
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR>
                    <TableLegacyTH className={"sticky top-0"}>
                      Payment date
                    </TableLegacyTH>
                    <TableLegacyTH className={"sticky top-0"}>
                      Amount
                    </TableLegacyTH>
                    <TableLegacyTH className={"sticky top-0"}>
                      Status
                    </TableLegacyTH>
                    <TableLegacyTH className={"sticky top-0"}></TableLegacyTH>
                  </TableLegacyTR>
                </TableLegacyTHead>
                <TableLegacyTBody>
                  {props.paymentList.map((payment) => {
                    const link = props.supplierStripeAccountId
                      ? buildStripeDashboardUrl({
                          type: "payment",
                          stripeAccountId: props.supplierStripeAccountId,
                          id: payment.paymentIntentId,
                        })
                      : undefined;
                    return (
                      <OrderPaymentRow
                        key={payment.id}
                        date={payment.createdAt}
                        paymentStatus={payment.status}
                        amount={payment.amount}
                        link={link}
                      />
                    );
                  })}
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>

            <div className="flex flex-col text-base text-zinc-700">
              <div className="font-bold">Remaining balance</div>
              <div>
                {remainingBalance === 0
                  ? remainingBalancePaidManually
                    ? "None (Paid manually)"
                    : "None"
                  : formatCurrency(remainingBalance)}
              </div>
            </div>
          </LegacyCardContent>
        ) : isImplicitProforma ? (
          <LegacyCardContent>
            <p>
              {props.supplierName} will issue the VAT invoice for this order
              outside of Sellar. They may request payment before your order is
              dispatched unless you already have agreed credit terms with them.
            </p>
          </LegacyCardContent>
        ) : (
          <LegacyCardContent>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <OrderPaymentStatusLegacy
                  paymentStatus={props.paymentStatus}
                  orderStage={props.stage}
                  stripeInvoiceId={props.stripeInvoiceId}
                  paymentDate={
                    props.paymentStatus === "paid"
                      ? props.paidAt
                      : (props.invoiceCreationStage === "dispatched" &&
                            props.dispatchedAt) ||
                          (props.invoiceCreationStage === "delivered" &&
                            props.deliveredAt) ||
                          props.invoiceCreationStage === "confirmed" ||
                          props.paymentTerms === "paymentOnDelivery"
                        ? props.paymentDue
                        : null
                  }
                  invoiceCreationStage={props.invoiceCreationStage}
                  confirmedAt={props.confirmedAt}
                  paymentTerms={props.paymentTerms}
                  orderPrice={props.orderPrice}
                  paymentType={props.paymentType}
                  hasPaymentLink={props.hasPaymentLink}
                />
                <OrderPaymentMethod
                  method={generateOrderPaymentMethodText({
                    credit: true,
                    fulfillmentType: props.fulfillmentType,
                    paymentType: props.paymentType,
                    paymentTerms: props.paymentTerms,
                    paid: props.paymentStatus === "paid",
                    orderPrice: props.orderPrice,
                    hasPaymentLink: props.hasPaymentLink,
                    hasPaymentLinkPayment: hasPayment,
                  })}
                />
              </div>
            </div>
          </LegacyCardContent>
        )}
      </div>
      {props.paymentLinkCode && (
        <ViewPaymentLinkModal
          active={viewPaymentLinkActive}
          onDismiss={() => setViewPaymentLinkActive(false)}
          orderId={props.orderId}
          paymentLinkCode={props.paymentLinkCode}
          retailerInvoiceEmails={props.retailerInvoiceEmails ?? []}
        />
      )}
    </>
  );
};

const OrderPaymentRow = ({
  date,
  amount,
  paymentStatus,
  link,
}: {
  date: Date;
  amount: number;
  paymentStatus: string;
  link?: string;
}) => {
  return (
    <TableLegacyTR>
      <TableLegacyTD>{formatDate(date, { format: "DD/MM/YY" })}</TableLegacyTD>
      <TableLegacyTD>{formatCurrency(amount)}</TableLegacyTD>
      <TableLegacyTD>{capitalize(paymentStatus)}</TableLegacyTD>
      <TableLegacyTD>
        <SupplierOnly>
          {link ? (
            <Anchor
              href={link}
              className="cursor-pointer underline"
              target="_blank"
            >
              View on stripe
            </Anchor>
          ) : null}
        </SupplierOnly>
      </TableLegacyTD>
    </TableLegacyTR>
  );
};
